'use client';

import { signIn, signOut, useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { mutate as swrMutate } from 'swr';

/**
 * Modern NextAuth-based authentication hook
 * Replaces the old localStorage-based useAuth
 */
export const useAuth = () => {
  const { data: session, status } = useSession();
  const router = useRouter();

  const isLoggedIn = status === 'authenticated';
  const isLoading = status === 'loading';
  const user = session?.user || null;

  // Migrate data when user logs in
  // Migration logic removed - using standard localStorage approach

  const login = useCallback(async (credentials) => {
    try {
      const result = await signIn('credentials', {
        email: credentials.email,
        password: credentials.password,
        turnstileToken: credentials.turnstileToken ?? undefined,
        redirect: false,
      });

      if (result?.error) {
        // Handle NextAuth specific errors
        if (result.error === 'CredentialsSignin') {
          return { success: false, error: 'Невалиден имейл или парола.' };
        }
        return { success: false, error: result.error };
      }

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const loginWithGoogle = useCallback(async () => {
    try {
      const result = await signIn('google', {
        redirect: false,
      });

      if (result?.error) {
        throw new Error(result.error);
      }

      return { success: true };
    } catch (error) {
      console.error('Google login error:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await signOut({ redirect: false });
      // Изчистваме SWR кеша за поръчки, за да не се показват данни от предишния потребител
      swrMutate((key) => typeof key === 'string' && key.startsWith('/api/orders'), undefined, {
        revalidate: false,
      });
      router.push('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [router]);

  const requireAuth = useCallback(
    (redirectTo = '/') => {
      if (!isLoggedIn && !isLoading) {
        router.push(redirectTo);
        return false;
      }
      return true;
    },
    [isLoggedIn, isLoading, router],
  );

  return {
    // Status
    isLoggedIn,
    isLoading,
    user,

    // Actions
    login,
    loginWithGoogle,
    logout,
    requireAuth,

    // Additional data
    role: user?.role || 'user',
    userId: user?.id,
    userEmail: user?.email,
    userName: user?.name,
  };
};
